import { useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { unitsFor, unitSymbol, useLatestVersions, useProducts, useStockLocations, useStockMovements } from '../data/catalog.ts'
import { useAssets, type AssetRow } from '../data/fleet.ts'
import { machineLabel } from '../data/planUi.ts'
import { useJobs, useAssignments } from '../data/plan.ts'
import { buildRefuelPayload, fuelProducts, locationLabel, machinesOfDay, PICKER_MACHINES, previewQuantity, refuelProblem, siteLocations, STOCK_PROBLEM_KEY, tankLocation } from '../data/stockUi.ts'
import { useWorkSessions } from '../data/work.ts'
import { fmtNum } from '../lib/format.ts'
import { formatLocal, localDate } from '../lib/time.ts'
import type { Pending } from '../sync/merge.ts'
import { BackLink, Banner, DisabledHint, errorText, Field, NotAllowed, Pill } from '../ui/components.tsx'
import { useAction, useWriteAccess } from '../ui/hooks.ts'
import { usePersonNames } from './measures/shared.tsx'
import { QuickReadings } from './MeterPanel.tsx'
import { useLang } from './catalogue/shared.tsx'

const RECENT = 5

/** M15-A, "Faire le plein" (docs/M15.md A, rule 2; the owner's default): this machine took N litres at the pump, typed by whoever refuels, never per job. A FACT
 *  of the field (stock.refuel@1, offline-first, projected, kiosk-allowed under the person's PIN): the fuel product (one is preselected), the quantity on the
 *  keypad with its unit, the tank (the site's first tank, else its default place), a note; "Relevé du compteur" beside it stays its own meter.record fact (I1,
 *  never derived). The last fill-ups of the machine under the form. Shown as a fold on the machine's page (AssetDetail) and as the page of Today's Terrain
 *  row (`/refuel`, `/assets/$assetId/refuel`: Z's routes), where the machine is picked first. */
export function RefuelForm({ asset }: { asset: Pending<AssetRow> }) {
  const { t } = useTranslation()
  const lang = useLang()
  const { send, site } = useClub()
  const engine = useEngineState()
  const access = useWriteAccess('stock.record')
  const { busy, error, run } = useAction()
  const products = useProducts(); const latest = useLatestVersions(); const locations = useStockLocations(); const movements = useStockMovements()
  const id = useRef(newId())
  const [productId, setProductId] = useState('')
  const [quantity, setQuantity] = useState('')
  const [unit, setUnit] = useState('')
  const [locationId, setLocationId] = useState('')
  const [note, setNote] = useState('')
  const [done, setDone] = useState<string | null>(null)
  const fuels = useMemo(() => fuelProducts(products ?? []), [products])
  const product = fuels.find((p) => p.id === productId) ?? fuels[0]
  const version = product ? latest?.get(product.id) : undefined
  const units = product ? unitsFor(product.base_unit_code, version?.density_kg_per_l != null) : []
  const chosenUnit = units.some((u) => u.code === unit) ? unit : product?.base_unit_code ?? ''
  const places = useMemo(() => siteLocations(locations ?? [], site.site_id), [locations, site.site_id])
  const tank = useMemo(() => tankLocation(locations ?? [], site.site_id), [locations, site.site_id])
  const chosenPlace = places.some((l) => l.id === locationId) ? locationId : tank?.id ?? ''
  const preview = product && quantity.trim() ? previewQuantity(quantity, chosenUnit, product, version) : null
  const inactive = asset.lifecycle != null && asset.lifecycle !== 'active'
  const problem = refuelProblem({ productId: product?.id ?? '', assetId: asset.id, quantity, unit: chosenUnit }, preview, fuels.length)
  const recent = useMemo(() => (movements ?? []).filter((m) => m.asset_id === asset.id && (m.reason === 'refuel' || m.reason === 'reversal')).sort((a, b) => (a.occurred_at < b.occurred_at ? 1 : -1)).slice(0, RECENT), [movements, asset.id])
  const names = usePersonNames()
  if (!products || !locations || !movements) return <p>{t('common.loading')}</p>
  if (!access.permitted) return <p className="meta" data-testid="refuel-not-allowed">{t('stock.refuel.notAllowed')}</p>
  const submit = (e: FormEvent) => {
    e.preventDefault(); setDone(null)
    if (!product || problem || inactive) return
    void run(async () => {
      await send('stock.refuel@1', buildRefuelPayload({ movementId: id.current, productId: product.id, assetId: asset.id, quantity, unit: chosenUnit, locationId: chosenPlace, note }))
      const qty = preview && preview.ok ? fmtNum(Number(preview.qtyBase), lang, 3) : quantity
      setDone(engine.online === false || !navigator.onLine ? t('stock.refuel.savedOffline') : t('stock.refuel.saved', { qty, unit: unitSymbol(product.base_unit_code, lang), tag: asset.readable_tag }))
      id.current = newId(); setQuantity(''); setNote('')
    })
  }
  return (
    <div className="flex flex-col gap-3" data-testid="refuel-panel" data-asset={asset.id}>
      <p className="meta">{t('stock.refuel.help')}</p>
      {inactive && <Banner tone="info" testId="refuel-retired">{t('stock.refuel.machineRetired')}</Banner>}
      {!inactive && (
        <form onSubmit={submit} noValidate className="flex flex-col gap-3" data-testid="refuel-form">
          <Field label={t('stock.refuel.product')} htmlFor="rf-product">
            <select id="rf-product" className="input" value={product?.id ?? ''} onChange={(e) => { setProductId(e.target.value); setUnit('') }} data-testid="refuel-product">
              {fuels.map((p) => <option key={p.id} value={p.id}>{p.name} ({unitSymbol(p.base_unit_code, lang)})</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <Field label={t('stock.refuel.liters')} htmlFor="rf-qty">
              <input id="rf-qty" className="input text-2xl" inputMode="decimal" autoComplete="off" value={quantity} onChange={(e) => { setQuantity(e.target.value); setDone(null) }} data-testid="refuel-quantity" />
            </Field>
            <Field label={t('stock.form.unit')} htmlFor="rf-unit">
              <select id="rf-unit" className="input" value={chosenUnit} onChange={(e) => setUnit(e.target.value)} data-testid="refuel-unit">
                {units.map((u) => <option key={u.code} value={u.code}>{unitSymbol(u.code, lang)}</option>)}
              </select>
            </Field>
          </div>
          {preview && !preview.ok && <p role="alert" className="text-sm font-medium text-red-700 dark:text-red-300" data-testid="refuel-preview-error">{preview.code === 'QUANTITY_INVALID' ? t('parts.quantityInvalid') : errorText(t, preview.code)}</p>}
          {preview?.ok && product && chosenUnit !== product.base_unit_code && <p className="text-sm font-semibold" data-testid="refuel-preview">= {fmtNum(Number(preview.qtyBase), lang, 3)} {unitSymbol(product.base_unit_code, lang)}</p>}
          <Field label={t('stock.refuel.tank')} htmlFor="rf-place">
            <select id="rf-place" className="input" value={chosenPlace} onChange={(e) => setLocationId(e.target.value)} data-testid="refuel-location">
              {places.map((l) => <option key={l.id} value={l.id}>{locationLabel(l, lang)}</option>)}
            </select>
          </Field>
          <Field label={t('stock.refuel.note')} htmlFor="rf-note"><input id="rf-note" className="input" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} data-testid="refuel-note" /></Field>
          {error && <Banner tone="error">{error}</Banner>}
          {done && <Banner tone="ok" testId="refuel-done">{done}</Banner>}
          {/* the hint waits while the success banner shows (the quantity is cleared after a save: "Saisissez la quantité" under "Plein enregistré" read as an error); the next keystroke clears the banner */}
          <DisabledHint show={!!problem && !done && !access.readOnlyMode} testId="refuel-hint">{problem ? t(STOCK_PROBLEM_KEY[problem]) : ''}</DisabledHint>
          <button className="btn btn-primary text-xl" disabled={busy || access.readOnlyMode || !!problem} data-testid="refuel-save">{busy ? t('common.saving') : t('stock.refuel.go')}</button>
        </form>
      )}
      {/* the meter reading beside the fill-up: its own fact, never derived (I1) */}
      <QuickReadings assetId={asset.id} title={t('stock.refuel.meter')} />
      <div className="flex flex-col gap-1" data-testid="refuel-history">
        <h3 className="text-sm font-semibold">{t('stock.refuel.history')}</h3>
        {recent.length === 0 && <p className="meta" data-testid="refuel-history-none">{t('stock.refuel.historyNone')}</p>}
        <ul className="flex flex-col gap-1">
          {recent.map((m) => {
            const who = names(m.actor_person_id)
            return (
              <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid="refuel-row" data-reason={m.reason}>
                <span><span className="font-semibold tabular-nums">{m.reason === 'reversal' ? '+' : ''}{fmtNum(Math.abs(Number(m.qty_base)), lang, 3)} {unitSymbol(m.base_unit_code, lang)}</span> · {formatLocal(m.occurred_at, site.timezone, 'dd/MM/yyyy HH:mm')}{who ? ` · ${who}` : ''}</span>
                {m.reason === 'reversal' ? <Pill tone="gray">{t('stock.reason.reversal')}</Pill> : m._pending ? <Pill tone="amber">{t('common.pendingSend')}</Pill> : null}
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

/** The page: the machine from the address (`/assets/$assetId/refuel`, `?asset=`), else picked here (the register's machines and vehicles in service, searched by tag). */
export function RefuelPage() {
  const { t } = useTranslation()
  const params = useParams({ strict: false }) as { assetId?: string }
  const search = useSearch({ strict: false }) as { asset?: string }
  const { personId, kiosk, site } = useClub()
  const access = useWriteAccess('stock.record')
  const assets = useAssets(); const sessions = useWorkSessions(); const assignments = useAssignments(); const jobs = useJobs()
  const [picked, setPicked] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const wanted = picked ?? params.assetId ?? search.asset ?? null
  const machines = useMemo(() => (assets ?? []).filter((a) => (a.kind === 'machine' || a.kind === 'vehicle') && a.lifecycle !== 'retired'), [assets])
  // M15a ergonomics review: the person's machines of the day first (the timers he opened today, his lines of today's jobs), then the rest by tag, and the list says how many more exist
  const me = kiosk?.session?.personId ?? personId
  const ordered = useMemo(() => {
    const today = localDate(new Date(), site.timezone)
    return machinesOfDay(machines, {
      personId: me, today, sessions: (sessions ?? []).map((s) => ({ person_id: s.person_id, asset_id: s.asset_id, started_local_date: localDate(s.started_at, site.timezone) })),
      assignments: assignments ?? [], jobDays: new Map((jobs ?? []).filter((j) => j.state !== 'cancelled').map((j) => [j.id, j.scheduled_local_date])),
    })
  }, [machines, me, sessions, assignments, jobs, site.timezone])
  const matches = useMemo(() => { const q = query.trim().toUpperCase(); return q ? machines.filter((a) => a.readable_tag.toUpperCase().includes(q) || machineLabel(a).toUpperCase().includes(q)).slice(0, PICKER_MACHINES) : [] }, [machines, query])
  if (!access.permitted) return <section className="flex flex-col gap-4" data-testid="refuel-page"><h1 className="text-2xl font-bold">{t('stock.refuel.title')}</h1><NotAllowed permission="stock.record" /></section>
  if (!assets) return <p>{t('common.loading')}</p>
  const asset = wanted ? assets.find((a) => a.id === wanted) : undefined
  const shown = query.trim() ? matches : [...ordered.mine, ...ordered.rest].slice(0, PICKER_MACHINES)
  const more = query.trim() ? 0 : machines.length - shown.length
  return (
    <section className="flex flex-col gap-4" data-testid="refuel-page">
      <BackLink to="/">← {t('common.today')}</BackLink>
      <h1 className="text-2xl font-bold">{t('stock.refuel.title')}</h1>
      {asset ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-lg font-semibold" data-testid="refuel-machine">{machineLabel(asset)}</p>
            {/* a field page (the pump, gloves): the plain 56-px button, not the office's 44 */}
            <button type="button" className="btn" onClick={() => { setPicked(null); setQuery('') }} data-testid="refuel-change">{t('stock.refuel.changeMachine')}</button>
          </div>
          <RefuelForm key={asset.id} asset={asset} />
        </>
      ) : (
        <div className="flex flex-col gap-3" data-testid="refuel-pick">
          <p className="meta">{t('stock.refuel.pickMachine')}</p>
          <Link to="/scan" className="btn"><span>{t('stock.refuel.scan')}</span></Link>
          <Field label={t('stock.refuel.searchMachine')} htmlFor="rf-search">
            <input id="rf-search" type="search" className="input" autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="refuel-search" />
          </Field>
          {query.trim() && matches.length === 0 && <p className="meta" data-testid="refuel-no-machine">{t('stock.refuel.noMachine')}</p>}
          {!query.trim() && ordered.mine.length > 0 && <p className="meta" data-testid="refuel-today-first">{t('stock.refuel.todayMachines')}</p>}
          <ul className="flex flex-col gap-2">
            {shown.map((a) => (
              <li key={a.id}><button type="button" className="btn w-full justify-start" onClick={() => setPicked(a.id)} data-testid="refuel-pick-machine" data-asset={a.id} data-today={ordered.mine.includes(a) ? '1' : '0'}>{machineLabel(a)}</button></li>
            ))}
          </ul>
          {more > 0 && <p className="meta" data-testid="refuel-more">{t('stock.refuel.moreMachines', { count: more })}</p>}
        </div>
      )}
    </section>
  )
}
